import { useState, useMemo } from 'react'
import { Check, ChevronsUpDown, Search } from 'lucide-react'
import { cn } from '@shared/lib/utils'
import { Button } from '@shared/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@shared/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover'

interface Currency {
  code: string
  name: string
  symbol: string
  flag: string
  popular?: boolean
}

// Popular currencies
const POPULAR_CURRENCIES: Currency[] = [
  { code: 'USD', name: 'US Dollar', symbol: '$', flag: '🇺🇸', popular: true },
  { code: 'EUR', name: 'Euro', symbol: '€', flag: '🇪🇺', popular: true },
  { code: 'GBP', name: 'British Pound', symbol: '£', flag: '🇬🇧', popular: true },
  { code: 'BGN', name: 'Bulgarian Lev', symbol: 'лв', flag: '🇧🇬', popular: true },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', flag: '🇯🇵', popular: true },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'Fr', flag: '🇨🇭', popular: true },
]

// All currencies
const ALL_CURRENCIES: Currency[] = [
  ...POPULAR_CURRENCIES,
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', flag: '🇦🇺' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$', flag: '🇨🇦' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', flag: '🇨🇳' },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', flag: '🇩🇰' },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', flag: '🇭🇰' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', flag: '🇮🇳' },
  { code: 'MXN', name: 'Mexican Peso', symbol: '$', flag: '🇲🇽' },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', flag: '🇳🇴' },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', flag: '🇳🇿' },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', flag: '🇵🇱' },
  { code: 'RON', name: 'Romanian Leu', symbol: 'lei', flag: '🇷🇴' },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', flag: '🇸🇪' },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', flag: '🇸🇬' },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', flag: '🇹🇭' },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', flag: '🇹🇷' },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', flag: '🇿🇦' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', flag: '🇧🇷' },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', flag: '🇨🇿' },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', flag: '🇭🇺' },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', flag: '🇮🇱' },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', flag: '🇰🇷' },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', flag: '🇲🇾' },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', flag: '🇵🇭' },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', flag: '🇷🇺' },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', flag: '🇦🇪' },
  { code: 'SAR', name: 'Saudi Riyal', symbol: 'ر.س', flag: '🇸🇦' },
]

interface CurrencySelectorProps {
  value: string
  onChange: (currency: string) => void
  label?: string
  className?: string
}

export function CurrencySelector({
  value,
  onChange,
  label = 'Currency',
  className
}: CurrencySelectorProps) {
  const [open, setOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const selectedCurrency = useMemo(
    () => ALL_CURRENCIES.find(c => c.code === value) || POPULAR_CURRENCIES[0],
    [value]
  )

  const filteredCurrencies = useMemo(() => {
    if (!searchQuery) {
      return ALL_CURRENCIES
    }

    const query = searchQuery.toLowerCase()
    return ALL_CURRENCIES.filter(
      currency =>
        currency.code.toLowerCase().includes(query) ||
        currency.name.toLowerCase().includes(query) ||
        currency.symbol.toLowerCase().includes(query)
    )
  }, [searchQuery])

  const popularCurrencies = useMemo(
    () => filteredCurrencies.filter(c => c.popular),
    [filteredCurrencies]
  )

  const otherCurrencies = useMemo(
    () => filteredCurrencies.filter(c => !c.popular),
    [filteredCurrencies]
  )

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
        </label>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-11 px-4 rounded-[10px]"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">{selectedCurrency.flag}</span>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{selectedCurrency.code}</span>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground text-sm">
                  {selectedCurrency.name}
                </span>
              </div>
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <CommandInput
                placeholder="Search currencies..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                className="flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>

            <CommandList className="max-h-[300px]">
              <CommandEmpty>No currency found.</CommandEmpty>

              {/* Popular Currencies */}
              {popularCurrencies.length > 0 && (
                <CommandGroup heading="Popular">
                  {popularCurrencies.map((currency) => (
                    <CommandItem
                      key={currency.code}
                      value={currency.code}
                      onSelect={() => {
                        onChange(currency.code)
                        setOpen(false)
                        setSearchQuery('')
                      }}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                    >
                      <span className="text-xl">{currency.flag}</span>
                      <div className="flex-1 flex items-center gap-2">
                        <span className="font-semibold">{currency.code}</span>
                        <span className="text-muted-foreground text-xs">
                          {currency.symbol}
                        </span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">
                          {currency.name}
                        </span>
                      </div>
                      <Check
                        className={cn(
                          'h-4 w-4',
                          value === currency.code ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {/* Other Currencies */}
              {otherCurrencies.length > 0 && (
                <CommandGroup heading="All Currencies">
                  {otherCurrencies.map((currency) => (
                    <CommandItem
                      key={currency.code}
                      value={currency.code}
                      onSelect={() => {
                        onChange(currency.code)
                        setOpen(false)
                        setSearchQuery('')
                      }}
                      className="flex items-center gap-3 px-3 py-2 cursor-pointer"
                    >
                      <span className="text-xl">{currency.flag}</span>
                      <div className="flex-1 flex items-center gap-2">
                        <span className="font-semibold">{currency.code}</span>
                        <span className="text-muted-foreground text-xs">
                          {currency.symbol}
                        </span>
                        <span className="text-muted-foreground">•</span>
                        <span className="text-sm text-muted-foreground">
                          {currency.name}
                        </span>
                      </div>
                      <Check
                        className={cn(
                          'h-4 w-4',
                          value === currency.code ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

// Export currency data for use elsewhere
export { ALL_CURRENCIES, POPULAR_CURRENCIES, type Currency }
